import { useEffect, useRef, useState } from 'react';
import type { Product, ProductAsset } from '@muidsoft/shared';
import { api, assetUrl } from '../lib/api.js';

interface Props {
  products: Product[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onClose: () => void;
  onCreated: (product: Product) => void;
  onUpdated?: (product: Product) => void;
  onDeleted?: (id: string) => void;
}

// ---------------------------------------------------------------------------
// Sub-komponen: Asset manager — tampil foto produk yang sudah ada + upload baru
// ---------------------------------------------------------------------------

function AssetManager({ productId }: { productId: string }) {
  const [assets, setAssets]       = useState<ProductAsset[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  async function loadAssets() {
    try {
      const res = await api.get<{ data: Product & { assets: ProductAsset[] } }>(`/api/bff/products/${productId}`);
      setAssets(res.data.assets ?? []);
    } catch { /* non-blocking */ }
  }

  useEffect(() => { loadAssets(); }, [productId]);

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadError(null);
    setUploading(true);
    const form = new FormData();
    form.append('file', file);
    form.append('productId', productId);
    form.append('kind', 'image');
    try {
      await api.upload('/api/bff/uploads', form);
      await loadAssets();
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload gagal');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  async function handleDelete(assetId: string) {
    try {
      await api.delete(`/api/bff/uploads/${assetId}`);
      setAssets((prev) => prev.filter((a) => a.id !== assetId));
    } catch { /* biarkan user coba lagi */ }
  }

  const imageAssets = assets.filter((a) => a.kind === 'image');

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <label style={{ fontSize: 13, fontWeight: 600 }}>Foto Produk</label>
        <button className="btn-ghost" style={{ fontSize: 12, padding: '4px 10px' }} onClick={() => inputRef.current?.click()} disabled={uploading}>
          {uploading ? 'Mengunggah...' : '+ Tambah Foto'}
        </button>
        <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" style={{ display: 'none' }} onChange={handleFileChange} />
      </div>
      {uploadError && <p style={{ color: 'var(--status-error)', fontSize: 12, marginBottom: 8 }}>{uploadError}</p>}
      {imageAssets.length === 0 ? (
        <p className="text-muted" style={{ fontSize: 12 }}>Belum ada foto. Tambah foto supaya AI bisa pakai sebagai referensi visual.</p>
      ) : (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {imageAssets.map((asset) => (
            <div key={asset.id} style={{ position: 'relative', width: 72, height: 72 }}>
              <img src={assetUrl(`/api/bff/uploads/${asset.id}`)} alt="Foto produk"
                style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--surface-3)' }} />
              <button onClick={() => handleDelete(asset.id)}
                style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%',
                  background: 'var(--status-error)', color: '#fff', border: 'none', cursor: 'pointer',
                  fontSize: 11, lineHeight: '20px', textAlign: 'center', padding: 0 }}
                aria-label="Hapus foto">×</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ProductSheet utama
// ---------------------------------------------------------------------------

export function ProductSheet({ products, selectedId, onSelect, onClose, onCreated, onUpdated, onDeleted }: Props) {
  type Mode = 'list' | 'create' | 'manage' | 'edit';
  const [mode, setMode]                     = useState<Mode>('list');
  const [managingProduct, setManagingProduct] = useState<Product | null>(null);

  // — state form create —
  const [name, setName]               = useState('');
  const [description, setDescription] = useState('');
  const [sellingPoints, setSellingPoints] = useState('');
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState<string | null>(null);

  // — state form edit —
  const [editName, setEditName]               = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editSellingPoints, setEditSellingPoints] = useState('');
  const [editSaving, setEditSaving]           = useState(false);
  const [editError, setEditError]             = useState<string | null>(null);

  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // ── Handlers ──────────────────────────────────────────────────────────────

  async function handleCreate() {
    if (!name.trim()) { setError('Nama produk wajib diisi'); return; }
    setSaving(true); setError(null);
    try {
      const res = await api.post<{ data: Product }>('/api/bff/products', {
        name: name.trim(),
        description: description.trim() || undefined,
        sellingPoints: sellingPoints.split('\n').map((s) => s.trim()).filter(Boolean),
      });
      onCreated(res.data);
      onSelect(res.data.id);
      setManagingProduct(res.data);
      setMode('manage');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Gagal membuat produk');
    } finally {
      setSaving(false);
    }
  }

  async function handleUpdate() {
    if (!managingProduct) return;
    if (!editName.trim()) { setEditError('Nama produk wajib diisi'); return; }
    setEditSaving(true); setEditError(null);
    try {
      const res = await api.patch<{ data: Product }>(`/api/bff/products/${managingProduct.id}`, {
        name: editName.trim(),
        description: editDescription.trim() || undefined,
        sellingPoints: editSellingPoints.split('\n').map((s) => s.trim()).filter(Boolean),
      });
      const updated = res.data;
      setManagingProduct(updated);
      onUpdated?.(updated);
      setMode('manage');
    } catch (err) {
      setEditError(err instanceof Error ? err.message : 'Gagal menyimpan perubahan');
    } finally {
      setEditSaving(false);
    }
  }

  async function handleDeleteProduct() {
    if (!managingProduct) return;
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    setDeleting(true); setError(null);
    try {
      await api.delete(`/api/bff/products/${managingProduct.id}`);
      onDeleted?.(managingProduct.id);
      backToList();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Gagal menghapus produk');
      setConfirmDelete(false);
    } finally {
      setDeleting(false);
    }
  }

  function openManage(product: Product) {
    setManagingProduct(product);
    setMode('manage');
    setError(null);
    setConfirmDelete(false);
  }

  function openEdit() {
    if (!managingProduct) return;
    setEditName(managingProduct.name);
    setEditDescription(managingProduct.description ?? '');
    setEditSellingPoints((managingProduct.sellingPoints ?? []).join('\n'));
    setEditError(null);
    setConfirmDelete(false);
    setMode('edit');
  }

  function backToList() {
    setManagingProduct(null);
    setMode('list');
    setError(null);
    setConfirmDelete(false);
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <div className="sheet__handle" />

        {/* ── MODE: LIST ── */}
        {mode === 'list' && (
          <>
            <h2 style={{ marginBottom: 12 }}>Pilih Produk</h2>
            {products.length === 0 && <p className="text-muted">Belum ada produk. Buat satu dulu.</p>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
              {products.map((p) => (
                <div key={p.id} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  border: `1px solid ${p.id === selectedId ? 'var(--gold-500)' : 'var(--surface-3)'}`,
                  borderRadius: 10, padding: '10px 12px',
                }}>
                  <button className="tap-target"
                    style={{ flex: 1, textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                    onClick={() => { onSelect(p.id); onClose(); }}>
                    <strong style={{ fontSize: 14 }}>{p.name}</strong>
                    {p.sellingPoints.length > 0 && (
                      <div className="text-muted" style={{ fontSize: 12, marginTop: 2 }}>
                        {p.sellingPoints.length} selling point
                      </div>
                    )}
                  </button>
                  {/* Tombol kelola (foto + edit + hapus) */}
                  <button className="btn-ghost" style={{ fontSize: 18, padding: '4px 8px', flexShrink: 0 }}
                    onClick={(e) => { e.stopPropagation(); openManage(p); }}
                    aria-label={`Kelola ${p.name}`} title="Kelola produk">
                    🖼
                  </button>
                </div>
              ))}
            </div>
            <button className="btn-primary" style={{ width: '100%' }} onClick={() => { setMode('create'); setError(null); }}>
              + Produk Baru
            </button>
          </>
        )}

        {/* ── MODE: CREATE ── */}
        {mode === 'create' && (
          <>
            <h2 style={{ marginBottom: 12 }}>Produk Baru</h2>
            <div className="field-group">
              <label>Nama produk</label>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="mis. Kaos Polos Navy" />
            </div>
            <div className="field-group">
              <label>Deskripsi</label>
              <textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
            </div>
            <div className="field-group">
              <label>Selling point (satu per baris)</label>
              <textarea rows={3} value={sellingPoints} onChange={(e) => setSellingPoints(e.target.value)} />
            </div>
            {error && <p style={{ color: 'var(--status-error)', fontSize: 13 }}>{error}</p>}
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn-ghost" style={{ flex: 1 }} onClick={() => setMode('list')}>Batal</button>
              <button className="btn-primary" style={{ flex: 2 }} onClick={handleCreate} disabled={saving}>
                {saving ? 'Menyimpan...' : 'Simpan & Kelola Foto'}
              </button>
            </div>
          </>
        )}

        {/* ── MODE: MANAGE ── */}
        {mode === 'manage' && managingProduct && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <button className="btn-ghost tap-target" style={{ padding: '4px 8px' }} onClick={backToList}>←</button>
              <h2 style={{ margin: 0, fontSize: 17, flex: 1 }}>{managingProduct.name}</h2>
              <button className="btn-ghost" style={{ fontSize: 12, padding: '4px 10px', flexShrink: 0 }} onClick={openEdit}>
                ✏️ Edit Info
              </button>
            </div>
            <AssetManager productId={managingProduct.id} />
            {error && <p style={{ color: 'var(--status-error)', fontSize: 13, marginTop: 10 }}>{error}</p>}
            <button className="btn-primary" style={{ width: '100%', marginTop: 20 }}
              onClick={() => { onSelect(managingProduct.id); onClose(); }}>
              ✓ Pakai Produk Ini
            </button>

            {/* ── Inline confirm delete — tidak pakai window.confirm() yang sering di-block mobile ── */}
            {!confirmDelete ? (
              <button className="btn-ghost"
                style={{ width: '100%', marginTop: 10, color: 'var(--status-error)', borderColor: 'var(--status-error)' }}
                onClick={() => setConfirmDelete(true)}>
                🗑 Hapus Produk Ini
              </button>
            ) : (
              <div style={{
                marginTop: 10, padding: '12px 14px', borderRadius: 10,
                border: '1.5px solid var(--status-error)',
                background: 'rgba(239,68,68,0.06)',
              }}>
                <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, color: 'var(--status-error)' }}>
                  Hapus "{managingProduct.name}"?
                </p>
                <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
                  Semua job dan data terkait ikut terhapus. Tidak bisa dibatalkan.
                </p>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn-ghost" style={{ flex: 1, fontSize: 13 }}
                    onClick={() => setConfirmDelete(false)} disabled={deleting}>
                    Batal
                  </button>
                  <button className="btn-ghost"
                    style={{ flex: 2, fontSize: 13, color: 'var(--status-error)', borderColor: 'var(--status-error)', opacity: deleting ? 0.6 : 1 }}
                    onClick={handleDeleteProduct} disabled={deleting}>
                    {deleting ? 'Menghapus...' : 'Ya, Hapus'}
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* ── MODE: EDIT ── */}
        {mode === 'edit' && managingProduct && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <button className="btn-ghost tap-target" style={{ padding: '4px 8px' }} onClick={() => { setMode('manage'); setEditError(null); setConfirmDelete(false); }}>←</button>
              <h2 style={{ margin: 0, fontSize: 17 }}>Edit Produk</h2>
            </div>
            <div className="field-group">
              <label>Nama produk</label>
              <input value={editName} onChange={(e) => setEditName(e.target.value)} />
            </div>
            <div className="field-group">
              <label>Deskripsi</label>
              <textarea rows={3} value={editDescription} onChange={(e) => setEditDescription(e.target.value)} />
            </div>
            <div className="field-group">
              <label>Selling point (satu per baris)</label>
              <textarea rows={4} value={editSellingPoints} onChange={(e) => setEditSellingPoints(e.target.value)} />
            </div>
            {editError && <p style={{ color: 'var(--status-error)', fontSize: 13 }}>{editError}</p>}
            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              <button className="btn-ghost" style={{ flex: 1 }} onClick={() => { setMode('manage'); setEditError(null); }} disabled={editSaving}>
                Batal
              </button>
              <button className="btn-primary" style={{ flex: 2 }} onClick={handleUpdate} disabled={editSaving || !editName.trim()}>
                {editSaving ? 'Menyimpan...' : 'Simpan Perubahan'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
